import React, { forwardRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import ModalHeader from "features/shared/components/modal/ModalHeader";
import ModalFooter from "features/shared/components/modal/ModalFooter";
import MessageDTO from "features/messages/dto/MessageDTO";
import { useQuery } from "data/hooks/useQuery";
import { Messages_Get } from "data/endpoints";
import { getRouteParams } from "features/shared/helpers/routeParams";
import IMessagesService from "../IMessagesService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { MessageStatus } from "../enums/messageStatus";
import { Dropdown } from "react-bootstrap";
import { isEmpty } from "features/shared/helpers/objects";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IFileService from "data/IFileService";
import DocumentSimplifiedDTO from "features/invoices/dtos/DocumentSimplifiedDTO";
import styles from "./messageModal.module.scss";
import RolesChecker from "features/customer/components/RolesChecker";
import { useTranslation } from "react-i18next";

interface Props {
  selectedMessageId: string;
  customerId: string;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const MessageModal = (props: Props) => {
  // translations
  const tLabels = useTranslation("messages\\messages").t;
  const tTerms = useTranslation(["shared\\terms"]).t;
  const tCategories = useTranslation("messages\\enums\\messageCategory").t;

  // DI
  const messageService = useInjection<IMessagesService>(
    IocTypes.MessagesService
  );

  const fileService = useInjection<IFileService>(IocTypes.FileService);

  // STORE
  const { data: fullMessage } = useQuery<MessageDTO>(
    {
      url: Messages_Get,
      routeParams: getRouteParams("messageId", props.selectedMessageId),
    },
    {},
    [props.show],
    undefined,
    {
      apply: true,
      condition: () => props.show === true
    }
  );

  // HANDLERS
  const handleOnHideOrCloseModal = async () => {
    messageService.loadAndStoreMessagesSimplified(props.customerId);
    props.setShow(false);
  };

  const handleOnClickDeleteButton = async () => {
    await messageService.updateMessageStatus(
      MessageStatus.Deleted,
      props.selectedMessageId
    );

    messageService.loadAndStoreMessagesSimplified(props.customerId);
    props.setShow(false);
  };

  const handleOnClickKeepButton = async () => {
    await messageService.updateMessageStatus(
      MessageStatus.Unread,
      props.selectedMessageId
    );

    messageService.loadAndStoreMessagesSimplified(props.customerId);

    props.setShow(false);
  };

  const handleOnClickDownloadFile = (document?: DocumentSimplifiedDTO) => {
    if (!document) return;
    fileService.downloadDocument(document);
  };

  // EFFECTS
  // Nastaví po otevření nepřečtenou zprávu jako přečtenou
  useEffect(() => {
    const canSetStatusToReaded =
      !isEmpty(fullMessage) &&
      fullMessage?.messageStatus === MessageStatus.Unread &&
      props.show;
    
    if (canSetStatusToReaded) {
      messageService.updateMessageStatus(
        MessageStatus.Read,
        props.selectedMessageId
      );
    }
  }, [props.show, fullMessage.messageStatus]);

  // SUB-RENDERS
  const renderFilesDropdownItems = (): JSX.Element[] => {
    let result: JSX.Element[] = [];

    result =
      fullMessage.documents?.map((val, i) => (
        <Dropdown.Item
          key={i}
          onClick={() => handleOnClickDownloadFile(val)}
          className="px-3"
        >
          <div className="d-flex align-items-center">
            <span className="mr-3 text-default">
              <FontAwesomeIcon icon="file-alt" />
            </span>
            <span className="text-truncate" title={val?.name}>
              {val?.name}
            </span>
          </div>
        </Dropdown.Item>
      )) ?? [];

    return result;
  };

  const CustomToggle = forwardRef((props: any, ref: any) => (
    <button
      ref={ref}
      className={`btn btn-outline-secondary dropdown-toggle ${styles.dropdownIcon}`}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  ));

  const DownloadCustomToggle = forwardRef((props: any, ref: any) => (
    <button
      ref={ref}
      className={`btn btn-outline-dark dropdown-toggle ${styles.dropdownIcon}`}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  ));

  // RENDER
  const isDocuments =
    fullMessage?.documents && fullMessage.documents.length > 0;

  return (
    <Modal
      show={props.show}
      onHide={handleOnHideOrCloseModal}
      scrollable={true}
      size="lg"
    >
      <ModalHeader
        title={fullMessage.messageCategory ? tCategories(fullMessage.messageCategory) : tLabels("messageModal.cardTitle")}
        onCloseClick={handleOnHideOrCloseModal}
      />
      <div className="modal-body p-4">
        <h3 className="mb-4 font-weight-bold">{fullMessage.title}</h3>
        <div
          dangerouslySetInnerHTML={{ __html: fullMessage.htmlBody! || "" }}
        />
      </div>

      <ModalFooter>
        <div className="d-flex flex-grow-1 justify-content-between">
          <div>
            {isDocuments && (
              <Dropdown drop="up">
                <Dropdown.Toggle as={DownloadCustomToggle} id="status-dropdown">
                  <span className="d-sm-none">
                    <FontAwesomeIcon icon="download" />
                  </span>
                  <span className="d-none d-sm-inline mr-1">
                    {tLabels("messageModal.downloadFile")}
                  </span>
                  <span className="mr-sm-1 ml-1 ml-sm-0">({fullMessage?.documents?.length})</span>
                </Dropdown.Toggle>

                <Dropdown.Menu className={styles.dropdownFilesWidth}>
                  {renderFilesDropdownItems()}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
          <div className="d-flex">
            <Dropdown drop="up">
              <Dropdown.Toggle as={CustomToggle} id="status-dropdown">
                <span className="d-sm-none">
                  <FontAwesomeIcon icon="cog" />
                </span>
                <span className="d-none d-sm-inline mr-1">{tLabels("messageModal.checkMessage")}</span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item disabled={true} className="px-3">
                  <div className="d-flex align-items-center text-default">
                    <span className="mr-3">
                      <FontAwesomeIcon icon="envelope-open" />
                    </span>
                    <span>{tLabels("messageModal.read")}</span>
                  </div>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={handleOnClickKeepButton}
                  className="px-3"
                >
                  <div className="d-flex align-items-center">
                    <span className="mr-3 text-secondary">
                      <FontAwesomeIcon icon="envelope" />
                    </span>
                    <span>{tLabels("messageModal.unread")}</span>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <RolesChecker enabledRoles="Contributor">
              <button
                type="button"
                onClick={handleOnClickDeleteButton}
                className="btn btn-outline-danger ml-2"
              >
                <span className="d-sm-none">
                  <FontAwesomeIcon icon="trash" />
                </span>
                <div className="d-none d-sm-block">{tTerms("Delete")}</div>
              </button>
            </RolesChecker>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default MessageModal;
